'use client'

import styled from '@emotion/styled'
import Image from 'next/image'
import Link from 'next/link'
import React, { useMemo, useState, useCallback, useEffect } from 'react'
import ReactModal from 'react-modal'
import { Footer } from '@/clients/components/molecules/Footer'
import { GlobalNav } from '@/components/organisms/GlobalNav'
import { AppBanner } from '@/clients/components/organisms/Banner/AppBanner'
import { useViewEvent } from '@/clients/firebase/analytics'
import {
  GameTitle,
  gameTitleInfos,
  getGameTitleDisplayName,
  getPopularGameTitles,
  gameTitles,
} from '@/clients/models/types/GameTitle'
import { useLocale } from '@/clients/hooks/useLocale'
import { colors } from '@/clients/styles/theme'
import {
  callSearchGameTitles,
  callRegisterGameTitle
} from '@/clients/firebase/bestGames/registerGameTitle'

type GameResult = {
  isInDB: boolean
  id: string
  name: string
  coverImageUrl: string
}

const MeProfilesSelectGame: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchResults, setSearchResults] = useState<GameResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedGame, setSelectedGame] = useState<GameResult | null>(null)
  const [isRegistering, setIsRegistering] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  useViewEvent('MyProfileCreateSelectGameTitleView')
  const { t } = useLocale()
  const [searchQuery, setSearchQuery] = useState('')
  const filterGameTitles = useCallback((query: string): GameTitle[] => {
    if (!query.trim()) {
      return getPopularGameTitles()
    }
    const normalizedQuery = query.toLowerCase()
    return gameTitles.filter((title) => {
      const gameInfo = gameTitleInfos[title]
      if (gameInfo.searchCandidates &&
          gameInfo.searchCandidates.some(candidate =>
            candidate.toLowerCase().includes(normalizedQuery))) {
        return true
      }
      return false
    })
  }, [])

  const filteredGameTitles = useMemo(() => {
    return filterGameTitles(searchQuery)
  }, [filterGameTitles, searchQuery])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }
  
  useEffect(() => {
    if (isModalOpen && searchQuery.trim() !== '') {
      searchGameTitles(searchQuery)
    }
  }, [isModalOpen, searchQuery])
  
  const searchGameTitles = async (query: string) => {
    setIsSearching(true)
    setErrorMessage(null)
    
    try {
      const response = await callSearchGameTitles({
        gameTitleSearchText: query,
      })
      
      const data = await response.data
      if (data.length === 0) {
        setErrorMessage(`「${query}」に一致するゲームは見つかりませんでした。`)
        setSearchResults([])
      } else {
        setSearchResults(
          data.map((game) => ({
            ...game,
            isInDB: false,
          }))
        )
      }
    } catch (error) {
      console.error(error)
      setErrorMessage('検索中にエラーが発生しました。')
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }
  
  const handleRequestRegisteringGameTitle = async (game: GameResult) => {
    setSelectedGame(game)
    setIsRegistering(true)
    setErrorMessage(null)
    
    try {
      await callRegisterGameTitle({
        imgUrl: game.coverImageUrl,
        gameTitle: game.name,
        igdbId: game.id,
      })
      
      setRegistrationSuccess(true)
    } catch (error) {
      console.error(error)
      setErrorMessage('ゲームの追加中にエラーが発生しました。')
    } finally {
      setIsRegistering(false)
    }
  }

  const gameBrands = useMemo(() => {
    return filteredGameTitles.map((title) => {
      return (
        <Link
          key={title}
          href={`/me/profiles/create/${title}`}
          style={{ textDecoration: 'none' }}
        >
          <Item>
            <IconContainer>
              <Image
                width={80}
                height={80}
                alt={title}
                unoptimized={true}
                src={gameTitleInfos[title]?.coverImgUrl ?? `https://storage.googleapis.com/nadare-production-static-images/static/images/gameTitles/icn_${title}.png`}
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'cover',
                }}
              />
            </IconContainer>
            <Text>{getGameTitleDisplayName(title)}</Text>
          </Item>
        </Link>
      )
    })
  }, [filteredGameTitles])

  return (
    <>
      <GlobalNav />
      <AppBanner />

      <Container>
        <ModalInner>
          <ModalTitle>
            自己紹介カードを作成して<br />ゲーム友達を探そう 😽
          </ModalTitle>
          <GameTitlesContainer>
            <Attention>{gameTitles.length}{t.種類の大人気ゲームに対応} 🎉</Attention>
          </GameTitlesContainer>
          
          <SelectGameText>ゲームタイトルを下から選んでください</SelectGameText>
          <SearchContainer>
            <SearchBox
              type="text"
              placeholder="検索"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <SearchIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#9E9E9E"/>
              </svg>
            </SearchIcon>
          </SearchContainer>
          
          {filteredGameTitles.length === 0 && searchQuery.trim() !== '' && (
            <NoResultsContainer>
              <MeltingImage
                src="/static/images/meechan/melting.png"
                alt="melting character"
              />
              <NoResultsText>「{searchQuery}」に一致するゲームが見つかりませんでした</NoResultsText>
              <RequestButton onClick={() => setIsModalOpen(true)}>
                ゲームタイトル追加をリクエストする
              </RequestButton>
            </NoResultsContainer>
          )}
          
          <GameBrandsContainer>{gameBrands}</GameBrandsContainer>
          
          <ReactModal
            isOpen={isModalOpen}
            ariaHideApp={false}
            onRequestClose={() => {
              setIsModalOpen(false)
              setSearchResults([])
              setSelectedGame(null)
              setRegistrationSuccess(false)
              setErrorMessage(null)
            }}
            style={{
              overlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999,
              },
              content: {
                top: '50%',
                left: '50%',
                right: 'auto',
                bottom: 'auto',
                marginRight: '-50%',
                transform: 'translate(-50%, -50%)',
                padding: '24px',
                borderRadius: '12px',
                textAlign: 'center',
                maxWidth: '400px',
              },
            }}
          >
            <ModalHeader>
              <ModalHeaderTitle>ゲームタイトル追加リクエスト</ModalHeaderTitle>
              <CloseModalButton
                onClick={() => {
                  setIsModalOpen(false)
                  setSearchResults([])
                  setSelectedGame(null)
                  setRegistrationSuccess(false)
                  setErrorMessage(null)
                }}
              >
                &times;
              </CloseModalButton>
            </ModalHeader>
            <ModalContent>
              {registrationSuccess ? (
                <SuccessContainer>
                  {selectedGame && (
                    <GameImageContainer>
                      <GameImage
                        src={selectedGame.coverImageUrl}
                        alt={selectedGame.name}
                      />
                    </GameImageContainer>
                  )}
                  <GameTitleText>
                    {selectedGame?.name}
                  </GameTitleText>
                  <SuccessMessage>
                    リクエストありがとう！<br />
                    近々対応いたします！
                  </SuccessMessage>
                  <CloseButton
                    onClick={() => {
                      setIsModalOpen(false)
                      setSearchResults([])
                      setSelectedGame(null)
                      setRegistrationSuccess(false)
                    }}
                  >
                    閉じる
                  </CloseButton>
                </SuccessContainer>
              ) : (
                <>
                  <SearchStatusContainer>
                    {isSearching ? (
                      <LoadingText>検索中...</LoadingText>
                    ) : errorMessage ? (
                      <ErrorText>{errorMessage}</ErrorText>
                    ) : searchResults.length === 0 ? (
                      <InfoText>「{searchQuery}」を検索しています...</InfoText>
                    ) : (
                      <ResultsText>{searchResults.length}件のゲームが見つかりました</ResultsText>
                    )}
                  </SearchStatusContainer>
                  
                  <GameResultsContainer>
                    {searchResults.map((game) => (
                      <GameResultItem key={game.id}>
                        <GameResultImage src={game.coverImageUrl} alt={game.name} />
                        <GameResultName>{game.name}</GameResultName>
                        <AddButton
                          onClick={() => handleRequestRegisteringGameTitle(game)}
                          disabled={isRegistering}
                        >
                          {isRegistering && selectedGame?.id === game.id ? '追加中...' : '追加する'}
                        </AddButton>
                      </GameResultItem>
                    ))}
                  </GameResultsContainer>
                </>
              )}
            </ModalContent>
          </ReactModal>
        </ModalInner>
      </Container>
      <Footer />
    </>
  )
}

export default MeProfilesSelectGame

const Container = styled.div`
  text-align: center;
  max-width: 420px;
  margin: 0 auto;
`

const ModalInner = styled.div``

const ModalTitle = styled.h1`
  margin-top: 24px;
  font-size: 20px;
  color: ${colors.text.primary};
  font-weight: bold;
  line-height: 1.3;
`

const GameBrandsContainer = styled.div`
  margin: 20px auto 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  max-width: 400px;
`

const GameTitlesContainer = styled.div`
  margin: 8px auto 0;
  text-align: center;
  width: 100%;
  max-width: 400px;
`

const Attention = styled.p`
  font-size: 12px;
  font-weight: bold;
  color: ${colors.text.primary};
  margin-bottom: 12px;
`

const Item = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 8px;
`

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  flex-grow: 0;
`

const Text = styled.div`
  width: 60px;
  text-align: center;
  font-size: 9px;
  font-weight: bold;
  margin-top: 4px;
  color: ${colors.text.primary};
`

const SelectGameText = styled.p`
  margin-top: 16px;
  font-size: 15px;
  color: ${colors.text.primary};
  line-height: 1.5;
`

const SearchContainer = styled.div`
  position: relative;
  margin: 20px auto;
  width: 90%;
  max-width: 400px;
`

const SearchBox = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border-radius: 50px;
  border: 1px solid #e0e0e0;
  font-size: 16px;
  background-color: #f5f5f5;
  color: ${colors.text.primary};
  outline: none;
  
  &::placeholder {
    color: #9e9e9e;
  }
  
  &:focus {
    border-color: ${colors.text.primary};
    background-color: #ffffff;
  }
`

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
`

const NoResultsContainer = styled.div`
  margin: 20px auto;
  padding: 16px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
`

const NoResultsText = styled.p`
  font-size: 14px;
  color: ${colors.text.primary};
  line-height: 1.5;
  margin-bottom: 16px;
`

const RequestButton = styled.button`
  background-color: ${colors.button.primary};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${colors.button.primary};
    opacity: 0.8;
  }
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const ModalHeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: ${colors.text.primary};
  margin: 0;
`

const CloseModalButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: ${colors.text.primary};
  }
`

const ModalContent = styled.div`
  padding: 8px 0;
  text-align: center;
`

const MeltingImage = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: 12px;
`

const SearchStatusContainer = styled.div`
  margin-bottom: 16px;
`

const InfoText = styled.p`
  font-size: 14px;
  color: ${colors.text.primary};
`

const LoadingText = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
`

const ErrorText = styled.p`
  font-size: 14px;
  color: ${colors.button.quinary};
`

const ResultsText = styled.p`
  font-size: 14px;
  font-weight: bold;
  color: ${colors.text.primary};
`

const GameResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
`

const GameResultItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  background-color: #f5f5f5;
`

const GameResultImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 12px;
`

const GameResultName = styled.p`
  font-size: 14px;
  font-weight: bold;
  flex: 1;
  color: ${colors.text.primary};
`

const AddButton = styled.button<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? colors.button.disabled : colors.button.primary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: bold;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    opacity: ${props => props.disabled ? 1 : 0.8};
  }
`

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
`

const GameImageContainer = styled.div`
  width: 100px;
  height: 100px;
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const GameImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const SuccessMessage = styled.p`
  font-size: 18px;
  font-weight: bold;
  color: ${colors.text.primary};
  margin-bottom: 8px;
  line-height: 1.4;
`

const GameTitleText = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin-bottom: 24px;
`

const CloseButton = styled.button`
  background-color: ${colors.button.primary};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`